import { SimpleDocCommand } from '../../apps/DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

export const entity_format: SimpleDocCommand = {
  id: 'entity_format',
  logicalName: 'entity_format',
  version: 'v2',

  description: 'Apply text formatting to an entity.',

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Sheet entity ID (sheet_*)',
        required: true,
        validator: (value: string) => {
          if (!value) return 'Entity ID is required';
          if (!value.startsWith('sheet_')) return 'entity_format only supports sheets (sheet_*). Entity ID must start with sheet_';
          return null;
        }
      },
      {
        name: 'target',
        type: 'object',
        description: 'Cell selector: { cell: "A1" } | { cells: [...] } | { range: "A1:C5" } | { row: 5 } | { column: "B" }',
        required: true,
        validator: (value: any) => {
          if (!value) return 'Target is required';
          const validKeys = ['cell', 'cells', 'range', 'row', 'column'];
          const hasValidKey = Object.keys(value).some(key => validKeys.includes(key));
          if (!hasValidKey) return `Target must include one of: ${validKeys.join(', ')}`;
          if (value.cell && typeof value.cell === 'string') {
            if (!/^[A-Z]+\d+$/.test(value.cell)) return 'cell must be in format "A1", "B2", "AA10" (column letters + row number)';
          }
          if (value.cells && Array.isArray(value.cells)) {
            for (const cell of value.cells) {
              if (!/^[A-Z]+\d+$/.test(cell)) return `cells array contains invalid reference "${cell}". Use format "A1", "B2", etc.`;
            }
          }
          if (value.range && typeof value.range === 'string') {
            if (!/^[A-Z]+\d+:[A-Z]+\d+$/.test(value.range)) return 'range must be in format "A1:C5" (start:end)';
          }
          if ('row' in value && (typeof value.row !== 'number' || value.row < 0)) return 'row must be a non-negative number';
          if (value.column && typeof value.column === 'string') {
            if (!/^[A-Z]+$/.test(value.column)) return 'column must be uppercase letter(s) (A, B, C, ..., AA, AB, ...)';
          }
          return null;
        }
      },
      {
        name: 'format',
        type: 'object',
        description: 'Formatting: { backgroundColor, textColor, fontSize, bold, italic, textAlign, etc. }',
        required: true,
        validator: (value: any) => {
          if (!value || typeof value !== 'object') return 'format must be an object with formatting properties';
          if (Object.keys(value).length === 0) return 'format must contain at least one formatting property';
          for (const prop of ['backgroundColor', 'textColor', 'color']) {
            if (value[prop] && typeof value[prop] === 'string') {
              if (!/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(value[prop])) return `${prop} must be a valid hex color (e.g., "#fff", "#ffeb3b")`;
            }
          }
          if (value.fontSize && typeof value.fontSize !== 'number') return 'fontSize must be a number (e.g., 12, 14, 16)';
          for (const prop of ['bold', 'italic', 'underline', 'strikethrough']) {
            if (value[prop] !== undefined && typeof value[prop] !== 'boolean') return `${prop} must be a boolean (true or false)`;
          }
          if (value.textAlign && !['left', 'center', 'right'].includes(value.textAlign)) return 'textAlign must be "left", "center", or "right"';
          return null;
        }
      }
    ]
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        return { type: 'cell_formatting', summary: result.message || 'Applied cell formatting', data: { entityId: result.data.entityId, affectedCells: result.data.affectedCells || [], cellCount: (result.data.affectedCells || []).length, format: result.data.format }, meta: { target: toolInput?.target, formatKeys: result.data.format ? Object.keys(result.data.format) : [] } };
      }
      return { type: 'tool_summary', summary: result?.message || 'Applied formatting', data: result?.data || {} };
    } catch (error) {
      console.warn('[entity_format] Error in formatViewData:', error);
      return { type: 'tool_summary', summary: 'Applied formatting', data: {} };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, target, format } = params;
    console.log('[entity_format] Applying formatting:', { entityId, target, format });
    try {
      const app = await getApplicationForEntity(entityId);
      if (!app) return { status: 'error', message: 'Sheet entity not found or not loaded', error: { code: 'entity_not_found', category: 'execution' as const, details: 'Entity not found in application registry' }, format: 'plaintext' };
      const result = await app.handleEntityFormat(target, format);
      const affectedItems = (result as any).affectedCells || (result as any).affectedNodes || [];
      return { status: 'success', message: `Formatting applied to ${affectedItems.length} cell${affectedItems.length === 1 ? '' : 's'}`, data: { entityId, affectedCells: affectedItems, format }, format: 'json' };
    } catch (error: any) {
      console.error('[entity_format] Error:', error);
      return { status: 'error', message: `Failed to apply formatting: ${error.message}`, error: { code: 'format_failed', category: 'execution' as const, details: error.message }, format: 'plaintext' };
    }
  }
};
